import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  History, Search, Eye, Pencil, Trash2, Lock, Unlock, RefreshCw, Store, Plus, Minus,
  X, AlertTriangle, CheckCircle2, ClipboardCheck, ShoppingCart, Bike, Utensils, Save,
  UploadCloud, Package, Info, Hash,
} from "lucide-react";
import { Card, Button, Badge, Spinner, Modal } from "../lib/ui";
import { ClosingSummaryContent } from "../components/ClosingSummary";
import { JournalPreview } from "../components/JournalPreview";
import { useToast } from "../lib/toast";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

const rp = (n) => "Rp " + Math.round(Number(n) || 0).toLocaleString("id-ID");
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "-");
const CH_ICON = { offline: Store, grab: Bike, gofood: Utensils };
const PAY_LABEL = { cash: "Tunai", transfer: "Transfer Bank", qris: "QRIS", debit: "Kartu Debit", credit: "Kartu Kredit" };
const SETTLEMENT_OFFLINE = { cash: "Cash On Hand", transfer: "BCA Bank Account", qris: "BRI Bank Account", debit: "BRI Bank Account", credit: "BRI Bank Account" };
const SETTLEMENT_ONLINE = { grab: "Grab OVO Merchant Wallet", gofood: "GoPay Merchant Wallet" };
const FinLine = ({ label, value, tone, strong }) => (
  <div className={`flex items-center justify-between py-0.5 ${strong ? "border-t border-line/60 pt-1.5 font-bold text-ink" : ""}`}>
    <span className={strong ? "text-ink" : "text-ink-soft"}>{label}</span>
    <span className={`font-semibold ${tone === "danger" ? "text-danger" : tone === "success" ? "text-emerald-600" : "text-ink"}`}>{value}</span>
  </div>
);
const fmtTime = (iso) => { try { return new Date(iso).toLocaleString("id-ID", { hour: "2-digit", minute: "2-digit" }); } catch { return "-"; } };
const fmtDateTime = (iso) => { try { return new Date(iso).toLocaleString("id-ID", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
const todayStr = () => new Date().toISOString().slice(0, 10);

const WRITE_ANY = ["OWNER", "SUPER_ADMIN"];
const FINALIZE_ROLES = ["BRANCH_STAFF", "OWNER", "SUPER_ADMIN"];
const UNLOCK_ROLES = ["BRANCH_MANAGER", "OWNER", "SUPER_ADMIN"];

// ---- shared status pill ----
function StatusBadge({ status }) {
  const finalized = status === "finalized";
  return (
    <Badge tone={finalized ? "info" : "warn"} data-testid={`status-${status}`}>
      {finalized ? <><Lock size={11} /> Terkunci</> : <><Pencil size={11} /> Draft</>}
    </Badge>
  );
}

// ============================ View detail modal ============================
function ViewEntryModal({ entryId, onClose }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/sales/pos-entry/${entryId}`); setData(data); }
      catch { toast.error("Gagal memuat detail transaksi"); onClose(); }
      finally { setLoading(false); }
    })();
  }, [entryId, onClose, toast]);

  return (
    <Modal open onClose={onClose} title="Detail Transaksi" size="lg">
      {loading || !data ? (
        <div className="flex h-40 items-center justify-center"><Spinner className="h-7 w-7" /></div>
      ) : (
        <div className="space-y-4" data-testid="view-entry-modal">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <span className="flex items-center gap-1.5 text-ink-soft"><span className="font-semibold text-ink">{fmtDateTime(data.time || data.created_at)}</span></span>
            <StatusBadge status={data.status} />
            {data.closing_number && <span className="flex items-center gap-1 text-ink-soft"><Hash size={12} className="text-ink-mute" /><span className="font-semibold text-ink" data-testid="view-closing-number">{data.closing_number}</span></span>}
            <span className="text-ink-soft">Kasir: <span className="font-semibold text-ink">{data.cashier_name || "-"}</span></span>
            <span className="text-ink-soft">Channel: <span className="font-semibold text-ink">{data.channel_label || data.channel}</span></span>
          </div>

          <div className="overflow-hidden rounded-xl border border-line">
            <table className="w-full text-sm">
              <thead className="bg-app text-left text-xs uppercase text-ink-mute">
                <tr><th className="px-3 py-2">Menu</th><th className="px-3 py-2">Kategori</th><th className="px-3 py-2 text-center">Qty</th></tr>
              </thead>
              <tbody className="divide-y divide-line">
                {(data.items || []).map((it) => (
                  <tr key={it.menu_item_id} data-testid={`view-item-${it.menu_item_id}`}>
                    <td className="px-3 py-2 font-medium text-ink">{it.name}</td>
                    <td className="px-3 py-2 text-ink-soft">{cap(it.station)}</td>
                    <td className="px-3 py-2 text-center font-semibold tabular-nums text-ink">{it.quantity}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-primary/20 bg-primary/5">
                  <td className="px-3 py-2 font-heading font-bold text-primary" colSpan={2}>Total Penjualan</td>
                  <td className="px-3 py-2 text-center font-heading font-bold text-primary">{data.total_qty} item</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl border border-line bg-app px-4 py-3" data-testid="view-finance-breakdown">
              <p className="mb-1.5 text-xs font-bold text-ink-soft">Rincian Keuangan</p>
              {data.channel === "offline" ? (
                <div className="text-sm">
                  <FinLine label="Bill (sebelum pajak)" value={rp(data.bill_amount ?? data.total_sales)} />
                  <FinLine label="Service Charge" value={`+ ${rp(data.service_charge || 0)}`} tone="success" />
                  <FinLine label="PB1 (titipan pajak)" value={`+ ${rp(data.pb1 || 0)}`} tone="danger" />
                  <FinLine label="Customer Payment" value={rp(data.customer_payment ?? data.total_sales)} strong />
                  <div className="mt-2 text-[11px] text-ink-mute" data-testid="view-settlement">Diterima di: <b>{SETTLEMENT_OFFLINE[data.payment_method] || "-"}</b>{data.payment_method ? ` · ${PAY_LABEL[data.payment_method] || data.payment_method}` : ""}</div>
                </div>
              ) : (
                <div className="text-sm">
                  <FinLine label="Gross Sales" value={rp(data.bill_amount ?? data.total_sales)} />
                  <FinLine label="Marketplace Fee" value={`- ${rp(data.marketplace_fee || 0)}`} tone="danger" />
                  <FinLine label="Net Settlement" value={rp((data.bill_amount ?? data.total_sales) - (data.marketplace_fee || 0))} strong />
                  <div className="mt-2 text-[11px] text-ink-mute" data-testid="view-settlement">Settlement ke: <b>{SETTLEMENT_ONLINE[data.channel] || "-"}</b></div>
                </div>
              )}
            </div>
            <div className="rounded-xl bg-app px-4 py-3">
              <p className="text-xs text-ink-mute">Bahan Terpotong Stok</p>
              <p className="font-heading text-xl font-bold text-ink">{data.products_consumed || 0} produk</p>
            </div>
          </div>

          <JournalPreview module="pos_sales" docId={entryId} />

          {data.missing_recipe && (data.missing_recipe_menus || []).length > 0 && (
            <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="view-missing-recipe">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <span><b>Resep belum diset:</b> {data.missing_recipe_menus.join(", ")}. Menu ini terjual tetapi stok tidak terpotong (BOM belum ada).</span>
            </div>
          )}

          {(data.consumed || []).length > 0 && (
            <div>
              <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-ink-soft"><Package size={13} /> Ringkasan Konsumsi Inventory</p>
              <div className="overflow-hidden rounded-xl border border-line">
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-line">
                    {data.consumed.map((c) => (
                      <tr key={c.product_id} data-testid={`consumed-${c.product_id}`}>
                        <td className="px-3 py-2 text-ink">{c.product_name}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-ink">{c.quantity} {c.unit || ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {data.note && (
            <div><p className="mb-1 text-xs font-semibold text-ink-soft">Catatan</p><p className="rounded-xl bg-app px-4 py-3 text-sm text-ink">{data.note}</p></div>
          )}

          {data.receipt_photo && (
            <div>
              <p className="mb-1 text-xs font-semibold text-ink-soft">Foto Struk</p>
              <img src={data.receipt_photo} alt="struk" className="max-h-72 rounded-xl border border-line object-contain" data-testid="view-receipt" />
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

// ============================ Edit modal (draft only) ============================
function EditEntryModal({ entry, menus, channels, onClose, onSaved }) {
  const toast = useToast();
  const [channel, setChannel] = useState(entry.channel);
  const [cart, setCart] = useState([]);
  const [totalSales, setTotalSales] = useState(String(entry.total_sales ?? ""));
  const [note, setNote] = useState(entry.note || "");
  const [receipt, setReceipt] = useState(null);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/sales/pos-entry/${entry.id}`);
        setCart((data.items || []).map((it) => ({ menu_item_id: it.menu_item_id, name: it.name, category: it.category, station: it.station, qty: it.quantity })));
        setReceipt(data.receipt_photo || null);
        setNote(data.note || "");
        setTotalSales(String(data.total_sales ?? ""));
        setChannel(data.channel);
      } catch { toast.error("Gagal memuat transaksi"); } finally { setLoading(false); }
    })();
  }, [entry.id, toast]);

  const results = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    const inCart = new Set(cart.map((c) => c.menu_item_id));
    return menus.filter((m) => m.name.toLowerCase().includes(q) && !inCart.has(m.id)).slice(0, 8);
  }, [search, menus, cart]);

  const addMenu = (m) => {
    setCart((prev) => [...prev, { menu_item_id: m.id, name: m.name, category: m.category, station: m.station, qty: 1 }]);
    setSearch("");
  };
  const setQty = (id, d) => setCart((prev) => prev.map((c) => c.menu_item_id === id ? { ...c, qty: Math.max(1, c.qty + d) } : c));
  const removeItem = (id) => setCart((prev) => prev.filter((c) => c.menu_item_id !== id));
  const totalQty = cart.reduce((s, c) => s + c.qty, 0);

  const onReceipt = (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Ukuran maksimal 5MB"); return; }
    const r = new FileReader(); r.onload = () => setReceipt(r.result); r.readAsDataURL(f);
  };

  const save = async () => {
    if (!cart.length) { toast.error("Tambahkan minimal satu menu"); return; }
    if (totalSales === "" || Number(totalSales) < 0) { toast.error("Isi Total Penjualan"); return; }
    setSaving(true);
    try {
      const { data } = await api.put(`/sales/pos-entry/${entry.id}`, {
        date: entry.date, channel,
        items: cart.map((c) => ({ menu_item_id: c.menu_item_id, quantity: c.qty })),
        total_sales: Number(totalSales), note: note || null, receipt_photo: receipt || null,
      });
      const missing = data?.consumption?.missing_recipe_menus || [];
      toast.success("Transaksi diperbarui · stok dihitung ulang");
      if (missing.length) toast.error(`Resep belum diset: ${missing.join(", ")}`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); } finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title="Edit Transaksi (Draft)" size="lg"
      footer={<><Button variant="ghost" onClick={onClose}>Batal</Button><Button onClick={save} loading={saving} disabled={loading} data-testid="edit-save-btn"><Save size={16} /> Simpan Perubahan</Button></>}>
      {loading ? (
        <div className="flex h-40 items-center justify-center" data-testid="edit-loading"><Spinner className="h-7 w-7" /></div>
      ) : (
      <div className="space-y-3" data-testid="edit-entry-modal">
        <div className="flex items-start gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
          <Info size={14} className="mt-0.5 shrink-0" /> Menyimpan perubahan akan otomatis mengembalikan stok lama & memotong ulang berdasarkan menu baru (via Recipe/BOM).
        </div>

        <div>
          <p className="mb-1.5 text-xs font-semibold text-ink-soft">Channel</p>
          <div className="grid grid-cols-3 gap-2">
            {channels.filter((c) => c.enabled || c.key === channel).map((c) => {
              const Icon = CH_ICON[c.key] || Store; const on = channel === c.key;
              return (
                <button key={c.key} onClick={() => setChannel(c.key)} data-testid={`edit-channel-${c.key}`}
                  className={`flex items-center justify-center gap-2 rounded-xl border-2 px-2 py-2.5 text-sm font-semibold transition ${on ? "border-primary bg-primary/5 text-primary" : "border-line text-ink-soft hover:border-ink-mute"}`}>
                  <Icon size={15} /> {c.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <p className="mb-1.5 text-xs font-semibold text-ink-soft">Tambah Menu</p>
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-mute" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari menu..." data-testid="edit-menu-search"
              className="w-full rounded-xl border border-line bg-app py-2.5 pl-9 pr-3 text-sm outline-none focus:border-primary" />
            {results.length > 0 && (
              <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-xl border border-line bg-surface shadow-lg">
                {results.map((m) => (
                  <button key={m.id} onClick={() => addMenu(m)} data-testid={`edit-menu-add-${m.id}`} className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-app">
                    <span className="font-medium text-ink">{m.name}</span><Plus size={13} className="text-primary" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-line">
          <table className="w-full text-sm">
            <thead className="bg-app text-left text-xs uppercase text-ink-mute"><tr><th className="px-3 py-2">Menu</th><th className="px-3 py-2 text-center">Qty</th><th className="px-3 py-2 text-center">Aksi</th></tr></thead>
            <tbody className="divide-y divide-line">
              {cart.length === 0 && <tr><td colSpan={3} className="px-3 py-6 text-center text-sm text-ink-mute">Belum ada menu.</td></tr>}
              {cart.map((c) => (
                <tr key={c.menu_item_id} data-testid={`edit-cart-row-${c.menu_item_id}`}>
                  <td className="px-3 py-2 font-medium text-ink">{c.name}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-center gap-1.5">
                      <button onClick={() => setQty(c.menu_item_id, -1)} data-testid={`edit-dec-${c.menu_item_id}`} className="rounded-lg border border-line p-1 text-ink-soft hover:bg-app"><Minus size={13} /></button>
                      <span className="w-7 text-center font-semibold tabular-nums text-ink">{c.qty}</span>
                      <button onClick={() => setQty(c.menu_item_id, 1)} data-testid={`edit-inc-${c.menu_item_id}`} className="rounded-lg border border-line p-1 text-ink-soft hover:bg-app"><Plus size={13} /></button>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-center"><button onClick={() => removeItem(c.menu_item_id)} data-testid={`edit-remove-${c.menu_item_id}`} className="rounded-lg p-1.5 text-danger-text hover:bg-red-50"><Trash2 size={15} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center justify-between rounded-xl bg-app px-3 py-2.5">
            <span className="text-xs font-medium text-ink-soft">Total Item</span>
            <span className="font-heading text-lg font-bold text-ink">{totalQty}</span>
          </div>
          <div>
            <label className="text-xs font-semibold text-ink-soft">Total Penjualan (Rp) <span className="text-primary">*</span></label>
            <input type="number" min="0" value={totalSales} onChange={(e) => setTotalSales(e.target.value)} data-testid="edit-total-sales"
              className="mt-0.5 w-full rounded-xl border border-line bg-surface px-3 py-2 text-right font-semibold text-ink outline-none focus:border-primary" />
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <p className="mb-1.5 text-xs font-semibold text-ink-soft">Foto Struk (Opsional)</p>
            {receipt ? (
              <div className="relative h-24 overflow-hidden rounded-xl border border-line">
                <img src={receipt} alt="struk" className="h-full w-full object-cover" />
                <button onClick={() => setReceipt(null)} data-testid="edit-receipt-remove" className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white"><X size={12} /></button>
              </div>
            ) : (
              <label data-testid="edit-receipt-photo" className="flex h-24 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-line text-ink-mute hover:border-primary hover:text-primary">
                <UploadCloud size={18} /><span className="text-[11px]">Upload foto struk</span>
                <input type="file" accept="image/*" className="hidden" onChange={onReceipt} />
              </label>
            )}
          </div>
          <div>
            <p className="mb-1.5 text-xs font-semibold text-ink-soft">Catatan (Opsional)</p>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="edit-note"
              className="w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-primary" />
          </div>
        </div>
      </div>
      )}
    </Modal>
  );
}

// ============================ Daily closing modal ============================
function ClosingModal({ date, branchId, canFinalize, onClose, onFinalized }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState("");
  const [finalizing, setFinalizing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const params = { date }; if (branchId) params.branch_id = branchId;
        const { data } = await api.get("/sales/closing-preview", { params });
        setData(data);
      } catch { toast.error("Gagal memuat pratinjau tutup harian"); onClose(); }
      finally { setLoading(false); }
    })();
  }, [date, branchId, onClose, toast]);

  const finalize = async () => {
    setFinalizing(true);
    try {
      const params = { date, closing_notes: notes || undefined }; if (branchId) params.branch_id = branchId;
      await api.post("/sales/finalize-day", null, { params });
      toast.success("Hari berhasil difinalisasi & dikunci");
      onFinalized();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memfinalisasi"); } finally { setFinalizing(false); }
  };

  return (
    <Modal open onClose={onClose} title={`Pratinjau Tutup Harian · ${date}`} size="xl"
      footer={data && !data.already_locked && canFinalize
        ? <><Button variant="ghost" onClick={onClose}>Batal</Button><Button variant="success" onClick={finalize} loading={finalizing} disabled={data.total_transactions === 0} data-testid="finalize-day-btn"><Lock size={16} /> Finalisasi & Kunci Hari</Button></>
        : <Button variant="ghost" onClick={onClose}>Tutup</Button>}>
      {loading || !data ? (
        <div className="flex h-40 items-center justify-center"><Spinner className="h-7 w-7" /></div>
      ) : (
        <div className="space-y-4" data-testid="closing-modal">
          {data.already_locked && (
            <div className="flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-800" data-testid="closing-locked">
              <Lock size={16} /> Hari ini sudah difinalisasi & dikunci.
            </div>
          )}

          <ClosingSummaryContent data={data} date={date} branchId={branchId} />

          {!data.already_locked && canFinalize && (
            <div>
              <label className="text-xs font-semibold text-ink-soft">Catatan Penutupan (Opsional)</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="closing-notes"
                placeholder="Catatan akhir hari, selisih kas, dll..."
                className="mt-0.5 w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-primary" />
              <p className="mt-1 flex items-center gap-1 text-[11px] text-amber-700"><AlertTriangle size={12} /> Setelah difinalisasi, semua transaksi terkunci. Hanya Manajer Cabang / Owner yang dapat membukanya.</p>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

// ============================ Main page ============================
export default function SalesHistory() {
  const toast = useToast();
  const { user } = useAuth();
  const role = user?.role;
  const needsBranchSelect = !user?.branch_id;   // Owner / Super Admin
  const canFinalize = FINALIZE_ROLES.includes(role);
  const canUnlock = UNLOCK_ROLES.includes(role);

  const [date, setDate] = useState(todayStr());
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [entries, setEntries] = useState([]);
  const [channels, setChannels] = useState([]);
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);

  const [fChannel, setFChannel] = useState("all");
  const [fCashier, setFCashier] = useState("all");
  const [fStatus, setFStatus] = useState("all");

  const [viewId, setViewId] = useState(null);
  const [editEntry, setEditEntry] = useState(null);
  const [delEntry, setDelEntry] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [closingOpen, setClosingOpen] = useState(false);
  const [unlocking, setUnlocking] = useState(false);

  const effectiveBranch = user?.branch_id || branchId;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date }; if (branchId) params.branch_id = branchId;
      const { data } = await api.get("/sales/pos-entry", { params });
      setEntries(data.entries || []);
    } catch { toast.error("Gagal memuat riwayat penjualan"); } finally { setLoading(false); }
  }, [date, branchId, toast]);

  useEffect(() => {
    (async () => {
      try {
        const reqs = [api.get("/sales/channels"), api.get("/menu-items?active_only=true")];
        if (needsBranchSelect) reqs.push(api.get("/branches"));
        const [ch, mn, br] = await Promise.all(reqs);
        setChannels(ch.data);
        setMenus(mn.data);
        if (needsBranchSelect && br) { setBranches(br.data); if (br.data.length) setBranchId(br.data[0].id); }
      } catch { toast.error("Gagal memuat data awal"); }
    })();
  }, [needsBranchSelect, toast]);

  useEffect(() => { load(); }, [load]);

  const cashiers = useMemo(() => Array.from(new Set(entries.map((e) => e.cashier_name).filter(Boolean))), [entries]);

  const filtered = useMemo(() => entries.filter((e) => {
    if (fChannel !== "all" && e.channel !== fChannel) return false;
    if (fCashier !== "all" && e.cashier_name !== fCashier) return false;
    if (fStatus !== "all" && (e.status || "draft") !== fStatus) return false;
    return true;
  }), [entries, fChannel, fCashier, fStatus]);

  const stats = useMemo(() => ({
    tx: filtered.length,
    items: filtered.reduce((s, e) => s + (e.total_qty || 0), 0),
    sales: filtered.reduce((s, e) => s + (e.total_sales || 0), 0),
    drafts: filtered.filter((e) => (e.status || "draft") !== "finalized").length,
    finalized: filtered.filter((e) => e.status === "finalized").length,
  }), [filtered]);

  const missingRecipes = useMemo(() => {
    const set = new Set();
    filtered.forEach((e) => (e.missing_recipe_menus || []).forEach((m) => set.add(m)));
    return Array.from(set);
  }, [filtered]);

  const dayLocked = entries.length > 0 && entries.every((e) => e.status === "finalized" || e.locked);
  const closingNumber = entries.find((e) => e.closing_number)?.closing_number;

  const canEditRow = (e) => {
    if (WRITE_ANY.includes(role)) return true;
    if (role === "BRANCH_STAFF") return (e.status || "draft") === "draft";
    return false;
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/sales/pos-entry/${delEntry.id}`);
      toast.success("Transaksi dihapus · stok dikembalikan");
      setDelEntry(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); } finally { setDeleting(false); }
  };

  const doUnlock = async () => {
    setUnlocking(true);
    try {
      const params = { date }; if (branchId) params.branch_id = branchId;
      await api.post("/sales/unlock-day", null, { params });
      toast.success("Hari berhasil dibuka kembali");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membuka kunci"); } finally { setUnlocking(false); }
  };

  return (
    <div data-testid="sales-history-page">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold text-ink"><History size={22} className="text-primary" /> Sales History</h1>
          <p className="text-sm text-ink-soft">Riwayat & pengelolaan transaksi penjualan harian.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {needsBranchSelect && (
            <select value={branchId} onChange={(e) => setBranchId(e.target.value)} data-testid="branch-select"
              className="rounded-xl border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-primary">
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="date-filter"
            className="rounded-xl border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-primary" />
          <Button variant="ghost" size="sm" onClick={load} data-testid="history-refresh"><RefreshCw size={14} /> Refresh</Button>
          {canUnlock && dayLocked && (
            <Button variant="outline" onClick={doUnlock} loading={unlocking} data-testid="unlock-day-btn"><Unlock size={15} /> Buka Kunci</Button>
          )}
          {canFinalize && (
            <Button onClick={() => setClosingOpen(true)} data-testid="open-closing-btn" disabled={dayLocked}><ClipboardCheck size={16} /> Tutup Kasir Harian</Button>
          )}
        </div>
      </div>

      {/* Summary bar */}
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-5" data-testid="summary-bar">
        {[
          { l: "Total Transaksi", v: stats.tx, t: "tx" },
          { l: "Total Item", v: stats.items, t: "items" },
          { l: "Total Penjualan", v: rp(stats.sales), t: "sales" },
          { l: "Draft", v: stats.drafts, t: "drafts" },
          { l: "Terkunci", v: stats.finalized, t: "finalized" },
        ].map((s) => (
          <Card key={s.l} className="!p-4" data-testid={`stat-${s.t}`}>
            <p className="text-[11px] text-ink-mute">{s.l}</p>
            <p className="font-heading text-xl font-bold text-ink">{s.v}</p>
          </Card>
        ))}
      </div>

      {dayLocked && role === "BRANCH_STAFF" && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800" data-testid="locked-banner">
          <Lock size={16} className="mt-0.5 shrink-0" />
          <span>Penjualan hari ini sudah difinalisasi & dikunci{closingNumber ? <> · <b>{closingNumber}</b></> : ""}. Hubungi Manajer Cabang atau Owner Anda bila diperlukan koreksi.</span>
        </div>
      )}

      {missingRecipes.length > 0 && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="history-missing-recipe">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span><b>Resep belum diset ({missingRecipes.length}):</b> {missingRecipes.join(", ")}. Menu ini terjual tetapi stok tidak terpotong.</span>
        </div>
      )}

      {/* Filters */}
      <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="filters">
        <select value={fChannel} onChange={(e) => setFChannel(e.target.value)} data-testid="filter-channel"
          className="rounded-xl border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-primary">
          <option value="all">Semua Channel</option>
          {channels.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <select value={fCashier} onChange={(e) => setFCashier(e.target.value)} data-testid="filter-cashier"
          className="rounded-xl border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-primary">
          <option value="all">Semua Kasir</option>
          {cashiers.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="filter-status"
          className="rounded-xl border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-primary">
          <option value="all">Semua Status</option>
          <option value="draft">Draft</option>
          <option value="finalized">Terkunci</option>
        </select>
      </div>

      {/* Table */}
      <Card data-testid="history-table">
        {loading ? (
          <div className="flex h-40 items-center justify-center"><Spinner className="h-7 w-7" /></div>
        ) : filtered.length === 0 ? (
          <p className="py-12 text-center text-sm text-ink-mute">Tidak ada transaksi untuk filter ini.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-line text-left text-xs uppercase text-ink-mute">
                <tr>
                  <th className="py-2 pr-3">Waktu</th>
                  <th className="py-2 pr-3">Channel</th>
                  <th className="py-2 pr-3">Kasir</th>
                  <th className="py-2 pr-3 text-center">Item</th>
                  <th className="py-2 pr-3 text-right">Penjualan</th>
                  <th className="py-2 pr-3 text-center">Status</th>
                  <th className="py-2 text-center">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {filtered.map((e) => (
                  <tr key={e.id} data-testid={`history-row-${e.id}`} className={e.missing_recipe ? "bg-amber-50/40" : ""}>
                    <td className="py-2.5 pr-3 text-ink-soft">{fmtTime(e.time || e.created_at)}</td>
                    <td className="py-2.5 pr-3 font-medium text-ink">{e.channel_label || e.channel}</td>
                    <td className="py-2.5 pr-3 text-ink-soft">{e.cashier_name || "-"}</td>
                    <td className="py-2.5 pr-3 text-center tabular-nums text-ink">{e.total_qty}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums font-semibold text-ink">{rp(e.total_sales)}</td>
                    <td className="py-2.5 pr-3 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <StatusBadge status={e.status || "draft"} />
                        {e.missing_recipe && <AlertTriangle size={14} className="text-amber-500" title="Ada menu tanpa resep" />}
                      </div>
                    </td>
                    <td className="py-2.5 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => setViewId(e.id)} data-testid={`view-btn-${e.id}`} className="rounded-lg p-1.5 text-ink-soft hover:bg-app" title="Lihat"><Eye size={15} /></button>
                        {canEditRow(e) && <button onClick={() => setEditEntry(e)} data-testid={`edit-btn-${e.id}`} className="rounded-lg p-1.5 text-ink-soft hover:bg-app" title="Edit"><Pencil size={15} /></button>}
                        {canEditRow(e) && <button onClick={() => setDelEntry(e)} data-testid={`delete-btn-${e.id}`} className="rounded-lg p-1.5 text-danger-text hover:bg-red-50" title="Hapus"><Trash2 size={15} /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {viewId && <ViewEntryModal entryId={viewId} onClose={() => setViewId(null)} />}
      {editEntry && <EditEntryModal entry={editEntry} menus={menus} channels={channels} onClose={() => setEditEntry(null)} onSaved={() => { setEditEntry(null); load(); }} />}
      {closingOpen && <ClosingModal date={date} branchId={effectiveBranch} canFinalize={canFinalize} onClose={() => setClosingOpen(false)} onFinalized={() => { setClosingOpen(false); load(); }} />}

      <Modal open={!!delEntry} onClose={() => setDelEntry(null)} title="Hapus Transaksi" size="sm"
        footer={<><Button variant="ghost" onClick={() => setDelEntry(null)}>Batal</Button><Button variant="danger" onClick={doDelete} loading={deleting} data-testid="confirm-delete-btn"><Trash2 size={15} /> Hapus</Button></>}>
        <div className="space-y-2 text-sm" data-testid="delete-confirm-modal">
          <p className="text-ink">Yakin ingin menghapus transaksi ini?</p>
          {delEntry && <p className="rounded-xl bg-app px-3 py-2 text-ink-soft">{delEntry.channel_label || delEntry.channel} · {delEntry.total_qty} item · {rp(delEntry.total_sales)}</p>}
          <p className="flex items-center gap-1 text-xs text-amber-700"><Info size={13} /> Stok yang sudah terpotong akan otomatis dikembalikan (ADJUSTMENT_IN).</p>
        </div>
      </Modal>
    </div>
  );
}
